/**
 * Unified amenity grading logic used by both BrowseCafeCard and CafeDetail.
 * Single source of truth for WiFi, Outlets, Seating, Food, and Parking grades.
 *
 * Presentation layer only — this never affects the numeric Grounded Score,
 * which is computed in src/lib/workScore.ts.
 */

export type LetterGrade = 'A' | 'B' | 'C' | 'D' | 'F' | 'U';

/** One-word quality label shown under the letter grade. */
export const GRADE_LABEL: Record<LetterGrade, string> = {
  A: 'Excellent',
  B: 'Good',
  C: 'Fair',
  D: 'Limited',
  F: 'None',
  U: 'Not verified yet',
};

/** Copy used wherever a feature has no data yet. */
export const NOT_VERIFIED_LABEL = 'Not verified yet';

export interface GradeResult {
  grade: LetterGrade;
  /** One-word quality label (Excellent / Good / Fair / Limited / None). */
  label: string;
  /** Factual descriptor that earned the grade, e.g. "Free lot". */
  subtitle: string;
  descriptor?: string;
  notes?: string;
}

function result(grade: LetterGrade, subtitle: string, extra?: Partial<GradeResult>): GradeResult {
  return { grade, label: GRADE_LABEL[grade], subtitle, ...extra };
}

interface AmenityInput {
  has_wifi?: boolean | null;
  wifi_type?: string | null;
  wifi_speed_avg?: number | null;
  wifi_reliability_avg?: number | null;
  wifi_notes?: string | null;
  outlet_availability?: string | null;
  seating_capacity?: string | null;
  seating_comfort_avg?: number | null;
  menu_type?: string | null;
  parking_available?: string | null;
  noise_level?: string | null;
}

/**
 * Returns true unless we have a definitive signal that the cafe has no WiFi.
 * Unknown / missing amenity data → true (don't aggressively hide WiFi UI).
 */
export function cafeHasWifi(
  amenities: Pick<AmenityInput, 'has_wifi' | 'wifi_type'> | null | undefined
): boolean {
  if (!amenities) return true;
  if (amenities.wifi_type === 'none') return false;
  if (amenities.has_wifi === false && !amenities.wifi_type) return false;
  return true;
}

/** WiFi: free A | paid B | none F */
export function getWifiGrade(amenities: AmenityInput | null | undefined): GradeResult {
  if (!amenities) return result('U', NOT_VERIFIED_LABEL);

  const notes = amenities.wifi_notes || undefined;
  const speed = amenities.wifi_speed_avg;
  const withSpeed = (base: string) =>
    speed && speed >= 25 ? `${base} · ${Math.round(speed)}Mbps` : base;

  // Confirmed absence only: wifi_type explicitly 'none', or has_wifi explicitly false.
  if (amenities.wifi_type === 'none' || (amenities.has_wifi === false && !amenities.wifi_type)) {
    return result('F', 'No WiFi', { notes });
  }

  if (amenities.wifi_type === 'free') {
    return result('A', withSpeed('Free WiFi'), { notes });
  }

  if (amenities.wifi_type === 'paid') {
    return result('B', withSpeed('Paid WiFi'), { notes });
  }

  if (amenities.has_wifi) {
    return result('B', 'WiFi available', { notes });
  }

  // Unknown (null/undefined) is not confirmed absence.
  return result('U', NOT_VERIFIED_LABEL, { notes });
}

/** Outlets: plenty A | some B | limited C | none F */
export function getOutletsGrade(amenities: AmenityInput | null | undefined): GradeResult {
  if (!amenities) return result('U', NOT_VERIFIED_LABEL);
  switch (amenities.outlet_availability) {
    case 'plenty': return result('A', 'Plenty of outlets');
    case 'some': return result('B', 'Some outlets available');
    case 'limited': return result('C', 'Limited outlets');
    case 'none': return result('F', 'No outlets');
    default: return result('U', NOT_VERIFIED_LABEL);
  }
}

/** Seating: large A | medium B | small C | none F */
export function getSeatingGrade(amenities: AmenityInput | null | undefined): GradeResult {
  if (!amenities) return result('U', NOT_VERIFIED_LABEL);
  switch (amenities.seating_capacity) {
    case 'large': return result('A', 'Spacious seating');
    case 'medium': return result('B', 'Medium seating');
    case 'small': return result('C', 'Limited seating');
    case 'none': return result('F', 'No seating');
    default: return result('U', NOT_VERIFIED_LABEL);
  }
}

/** Food is not part of the Grounded Score — kept for the Amenities section. */
export function getFoodGrade(amenities: AmenityInput | null | undefined): GradeResult {
  if (!amenities?.menu_type) {
    return result('C', 'Beverages only', { descriptor: 'Coffee and drinks, no food' });
  }
  if (amenities.menu_type === 'full_food') {
    return result('A', 'Full food menu', { descriptor: 'Proper meals to fuel your work day' });
  }
  if (amenities.menu_type === 'pastries_and_drinks') {
    return result('B', 'Light bites available', { descriptor: 'Snacks and drinks to keep you going' });
  }
  return result('C', 'Beverages only', { descriptor: 'Coffee and drinks, no food' });
}

/**
 * Parking:
 * free lot A | free garage B | street B | paid lot C | paid garage C | metered D | none F
 * Comma-separated values resolve to the best available option.
 */
const PARKING_RANK: Record<string, { grade: LetterGrade; subtitle: string; rank: number }> = {
  free_lot: { grade: 'A', subtitle: 'Free lot', rank: 6 },
  lot: { grade: 'A', subtitle: 'Free lot', rank: 6 },
  free_garage: { grade: 'B', subtitle: 'Free garage', rank: 5 },
  free_street: { grade: 'B', subtitle: 'Street parking', rank: 4 },
  street: { grade: 'B', subtitle: 'Street parking', rank: 4 },
  garage: { grade: 'C', subtitle: 'Paid garage', rank: 2 },
  paid_lot: { grade: 'C', subtitle: 'Paid lot', rank: 3 },
  paid_garage: { grade: 'C', subtitle: 'Paid garage', rank: 2 },
  paid_street: { grade: 'C', subtitle: 'Paid street', rank: 2 },
  metered: { grade: 'D', subtitle: 'Metered parking', rank: 1 },
};

export function getParkingGrade(amenities: AmenityInput | null | undefined): GradeResult {
  if (!amenities?.parking_available) return result('U', NOT_VERIFIED_LABEL);
  if (amenities.parking_available === 'none') {
    return result('F', 'No parking', { descriptor: 'Plan to walk, bike, or rideshare' });
  }

  const options = amenities.parking_available
    .split(',')
    .map((p) => p.trim().toLowerCase())
    .filter(Boolean);

  let best: { grade: LetterGrade; subtitle: string; rank: number } | null = null;
  for (const opt of options) {
    const match = PARKING_RANK[opt];
    if (match && (!best || match.rank > best.rank)) best = match;
  }

  if (!best) return result('U', NOT_VERIFIED_LABEL);
  return result(best.grade, best.subtitle);
}

/** Map a letter grade to a browse-card style grade string for highlight chips */
export function letterGradeToBrowseGrade(grade: LetterGrade): string | null {
  switch (grade) {
    case 'A': return 'A';
    case 'B': return 'B';
    case 'C': return 'C';
    case 'D': return 'D';
    case 'F': return null;
    case 'U': return null;
  }
}
